import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../i18n/useI18n";

interface ApplicationErrorBoundaryProps {
  children?: ReactNode;
  resetKey?: string;
  t?: (value: string) => string;
}

interface ApplicationErrorBoundaryState {
  error: Error | null;
}

export class ApplicationErrorBoundary extends Component<ApplicationErrorBoundaryProps, ApplicationErrorBoundaryState> {
  state: ApplicationErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ApplicationErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Необработанная ошибка страницы:", error, errorInfo);
  }

  componentDidUpdate(previousProps: ApplicationErrorBoundaryProps) {
    if (
      this.state.error &&
      previousProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ error: null });
    }
  }

  reloadPage = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const translate = this.props.t || ((value) => value);

    return (
      <main className="application-error" role="alert">
        <h1>{translate("Страница не загрузилась")}</h1>
        <p>
          {translate("Произошла непредвиденная ошибка. Ваши оценки не удалены. Попробуйте обновить страницу.")}
        </p>
        <div className="application-error__actions">
          <button type="button" onClick={this.reloadPage}>
            {translate("Обновить страницу")}
          </button>
          <a href="/">{translate("На главную")}</a>
        </div>
      </main>
    );
  }
}

const RouteErrorBoundary = ({ children }: { children?: React.ReactNode }) => {
  const { t } = useI18n();
  const location = useLocation();
  const resetKey = `${location.pathname}${location.search}${location.hash}`;

  return (
    <ApplicationErrorBoundary resetKey={resetKey} t={t}>
      {children}
    </ApplicationErrorBoundary>
  );
};

export default RouteErrorBoundary;
